import React from 'react';
import {
  ArrowUturnLeftIcon,
  ArrowUturnRightIcon,
  ArrowDownTrayIcon,
  CloudArrowUpIcon,
  MagnifyingGlassPlusIcon,
  MagnifyingGlassMinusIcon,
  PhotoIcon,
} from '@heroicons/react/24/outline';

const MODES = [
  { id: 'overlay', label: '2D Overlay' },
  { id: 'mockup', label: 'Mockup' },
  { id: 'template', label: 'Templates' },
  { id: 'ai', label: 'AI Design' },
];

const SmileDesignerToolbar = ({
  activeMode,
  onModeChange,
  onUndo,
  onRedo,
  canUndo,
  canRedo,
  onSave,
  onExport,
  saving,
  selectedPhase,
  phases,
  onPhaseChange,
  photos,
  selectedPhoto,
  onPhotoChange,
  designs,
  selectedDesignId,
  onDesignChange,
  fabricCanvas,
  onUploadPhoto,
}) => {
  const handleZoom = (direction) => {
    if (!fabricCanvas) return;
    const zoom = fabricCanvas.getZoom();
    const newZoom = direction === 'in' ? zoom * 1.2 : zoom / 1.2;
    fabricCanvas.setZoom(Math.min(Math.max(newZoom, 0.3), 5));
    fabricCanvas.renderAll();
  };

  const handleFitZoom = () => {
    if (!fabricCanvas) return;
    fabricCanvas.setZoom(1);
    fabricCanvas.absolutePan({ x: 0, y: 0 });
    fabricCanvas.renderAll();
  };

  const phaseNames = {
    initial: 'Initial',
    postOrtho: 'Post-Ortho',
    provisional: 'Provisional',
    final: 'Final',
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg mb-3 px-3 py-2">
      {/* Top Row: Mode tabs + Save/Export */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        {/* Mode Tabs */}
        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5">
          {MODES.map(mode => (
            <button
              key={mode.id}
              onClick={() => onModeChange(mode.id)}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                activeMode === mode.id
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {mode.label}
            </button>
          ))}
        </div>

        {/* Photo Selection */}
        <div className="flex items-center gap-2">
          <select
            value={selectedPhase}
            onChange={(e) => onPhaseChange(e.target.value)}
            className="text-xs border border-gray-200 rounded-md px-2 py-1.5 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Select Phase</option>
            {phases.map(phase => (
              <option key={phase.id} value={phase.id}>
                {phaseNames[phase.phaseType] || phase.phaseType}
              </option>
            ))}
          </select>

          <select
            value={selectedPhoto?.id || ''}
            onChange={(e) => {
              const photo = photos.find(p => p.id === e.target.value);
              onPhotoChange(photo || null);
            }}
            className="text-xs border border-gray-200 rounded-md px-2 py-1.5 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={!selectedPhase}
          >
            <option value="">Select Photo</option>
            {photos.map(photo => (
              <option key={photo.id} value={photo.id}>
                {(photo.category || 'Unknown').replace(/_/g, ' ')}
              </option>
            ))}
          </select>

          <button
            onClick={onUploadPhoto}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 transition"
            title="Upload a photo from your device"
          >
            <PhotoIcon className="w-3.5 h-3.5" />
            Upload
          </button>
        </div>

        {/* Saved Designs */}
        <div className="flex items-center gap-2">
          <select
            value={selectedDesignId || ''}
            onChange={(e) => onDesignChange(e.target.value || null)}
            className="text-xs border border-gray-200 rounded-md px-2 py-1.5 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">New Design</option>
            {designs.map(d => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </select>
        </div>

        {/* Undo/Redo + Zoom */}
        <div className="flex items-center gap-1">
          <button
            onClick={onUndo}
            disabled={!canUndo}
            className="p-1.5 text-gray-500 hover:text-gray-700 disabled:opacity-30 rounded hover:bg-gray-100"
            title="Undo (Ctrl+Z)"
          >
            <ArrowUturnLeftIcon className="w-4 h-4" />
          </button>
          <button
            onClick={onRedo}
            disabled={!canRedo}
            className="p-1.5 text-gray-500 hover:text-gray-700 disabled:opacity-30 rounded hover:bg-gray-100"
            title="Redo (Ctrl+Shift+Z)"
          >
            <ArrowUturnRightIcon className="w-4 h-4" />
          </button>

          <div className="w-px h-5 bg-gray-200 mx-1" />

          <button
            onClick={() => handleZoom('out')}
            className="p-1.5 text-gray-500 hover:text-gray-700 rounded hover:bg-gray-100"
            title="Zoom Out"
          >
            <MagnifyingGlassMinusIcon className="w-4 h-4" />
          </button>
          <button
            onClick={handleFitZoom}
            className="px-2 py-1 text-xs text-gray-500 hover:text-gray-700 rounded hover:bg-gray-100"
            title="Reset Zoom"
          >
            Fit
          </button>
          <button
            onClick={() => handleZoom('in')}
            className="p-1.5 text-gray-500 hover:text-gray-700 rounded hover:bg-gray-100"
            title="Zoom In"
          >
            <MagnifyingGlassPlusIcon className="w-4 h-4" />
          </button>
        </div>

        {/* Save + Export */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => onExport('png')}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition"
            title="Export as PNG"
          >
            <ArrowDownTrayIcon className="w-3.5 h-3.5" />
            Export
          </button>
          <button
            onClick={onSave}
            disabled={saving}
            className="inline-flex items-center gap-1.5 px-4 py-1.5 text-xs font-semibold text-white bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg hover:from-blue-700 hover:to-indigo-700 transition shadow-sm disabled:opacity-50"
            title="Save (Ctrl+S)"
          >
            <CloudArrowUpIcon className="w-3.5 h-3.5" />
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SmileDesignerToolbar;
